import { api, localDateTime } from './api';
import { planVoiceCapture } from './voiceCapturePlan';
import { flagNoteForReview, rememberVoiceDraft, clearVoiceDraft } from './noteReview';
import { scheduleCorrection } from './noteScheduling';

export async function beginVoiceCapture(text, { token, onChange }, client = api) {
  const plan = planVoiceCapture(text);
  const options = { token, clientNow: localDateTime(plan.reference) };
  // Durably capture ALL words before organizing. No question or confirmation.
  let original = await client.create(plan.original, 'voice', options);
  let safetyIncomplete = false;
  // The shared server normalizes text on POST (including removing spoken
  // dates). Its explicit PATCH preserves text. Restore every word before
  // splitting so a failed child request cannot discard another item's date.
  const preserve = {};
  if (original.text !== plan.original) preserve.text = plan.original;
  if (original.category !== plan.items[0].intent.category) preserve.category = plan.items[0].intent.category;
  if (plan.items.length === 1) Object.assign(preserve, scheduleCorrection(original, plan.items[0].schedule));
  if (Object.keys(preserve).length) {
    try { original = await client.update(original.id, preserve, options); }
    catch {
      safetyIncomplete = true;
      if (original.text !== plan.original) rememberVoiceDraft(original, plan.original);
      flagNoteForReview(original);
    }
  }
  onChange({ upsert: [original], remove: [] });
  const batch = { notes: [original], original, plan, token, actionTaken: false, closed: false };
  const active = new Map([[original.id, original]]);
  const put = note => { active.set(note.id, note); onChange({ upsert: [note], remove: [] }); };
  const correctType = async (note, intent, rawText, schedule) => {
    let saved = note;
    const patch = {};
    if (note.category !== intent.category) patch.category = intent.category;
    if (rawText && note.text !== rawText) patch.text = rawText;
    Object.assign(patch, scheduleCorrection(note, schedule));
    if (Object.keys(patch).length) {
      try { saved = await client.update(note.id, patch, options); clearVoiceDraft(saved); }
      catch {
        if (rawText && note.text !== rawText) rememberVoiceDraft(saved, rawText);
        flagNoteForReview(saved); put(saved); return saved;
      }
    }
    if (intent.needsReview) flagNoteForReview(saved);
    put(saved); return saved;
  };
  const preserveOriginal = async () => {
    // If splitting cannot finish, keep the entire original wording. Remove
    // only children created by this operation, never any other user's notes.
    // A failed PATCH response does not prove the server failed to write it.
    // Restore and CONFIRM the full original before deleting any children.
    try {
      const restored = await client.update(original.id, {
        text: plan.original, category: plan.items[0].intent.category,
        scheduled_at: original.scheduled_at || null, detected_date: original.detected_date || null,
      }, options);
      if (restored?.id !== original.id || restored.text !== plan.original) throw new Error('Original capture restoration was not confirmed');
      original = restored; clearVoiceDraft(original); put(original);
    } catch {
      // Keep confirmed children as well as the recovery draft. Duplicates
      // marked for review are safer than deleting the only remaining words.
      rememberVoiceDraft(original, plan.original);
      for (const note of active.values()) { flagNoteForReview(note); put(note); }
      batch.notes = [...active.values()]; batch.needsReview = true;
      return batch.notes;
    }
    await Promise.all([...active.values()].filter(n => n.id !== original.id).map(async note => {
      try { await client.remove(note.id, options); active.delete(note.id); onChange({ upsert: [], remove: [note.id] }); }
      catch (error) {
        if (error.status === 404) { active.delete(note.id); onChange({ upsert: [], remove: [note.id] }); }
        else flagNoteForReview(note);
      }
    }));
    flagNoteForReview(original); put(original);
    batch.notes = [...active.values()]; batch.needsReview = true;
    return batch.notes;
  };
  batch.done = (async () => {
    if (safetyIncomplete) { batch.needsReview = true; return batch.notes; }
    if (plan.items.length === 1) {
      batch.notes = [await correctType(original, plan.items[0].intent, plan.original, plan.items[0].schedule)];
      return batch.notes;
    }
    const children = await Promise.allSettled(plan.items.slice(1).map(async item => {
      const note = await client.create(item.text, 'voice', options);
      // Track confirmed ownership before any further API call can fail.
      put(note); return correctType(note, item.intent, item.text, item.schedule);
    }));
    if (children.some(result => result.status === 'rejected')) return preserveOriginal();
    const first = plan.items[0];
    try {
      const patch = { text: first.text, category: first.intent.category, scheduled_at: first.schedule.value, detected_date: first.schedule.value ? null : first.schedule.date || null };
      const note = await client.update(original.id, patch, options);
      if (first.intent.needsReview) flagNoteForReview(note);
      put(note);
      batch.notes = [note, ...children.map(result => result.value)];
      return batch.notes;
    } catch { return preserveOriginal(); }
  })().catch(preserveOriginal);
  return batch;
}

export async function undoVoiceBatch(batch, client = api) {
  const notes = await batch.done;
  const results = await Promise.allSettled(notes.map(note => client.remove(note.id, { token: batch.token })));
  const removed = [], remaining = [];
  results.forEach((result, index) => {
    if (result.status === 'fulfilled' || result.reason?.status === 404) removed.push(notes[index].id);
    else remaining.push(notes[index]);
  });
  batch.notes = remaining;
  batch.done = Promise.resolve(remaining);
  return { removed, remaining };
}